import type { FormikErrors } from 'formik';
import {
  TOWPRO_TOW_REASONS,
  type TowProDispatchRequest,
  type TowProRequestResponse,
  type TowProSubType,
  type TowProVehicleOwnerData,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import { VIN_LENGTH, type TowProFormValues } from './towProFormConstants';

export const REQUIRED_ERROR = 'Required';
export const ADDRESS_REQUIRED_ERROR = 'Address is required';
export const VIN_LENGTH_ERROR = `VIN must be ${VIN_LENGTH} characters`;
export const AGENCY_ID_ERROR = 'Agency ID must be a number';

export function validateTowProForm(
  values: TowProFormValues,
): FormikErrors<TowProFormValues> {
  const errors: FormikErrors<TowProFormValues> = {};

  if (
    values.address.trim().length === 0 ||
    values.latitude == null ||
    values.longitude == null
  ) {
    errors.address = ADDRESS_REQUIRED_ERROR;
  }
  if (!values.region_id) {
    errors.region_id = REQUIRED_ERROR;
  }
  if (!values.reason) {
    errors.reason = REQUIRED_ERROR;
  }
  if (values.vin.trim().length > 0 && values.vin.trim().length !== VIN_LENGTH) {
    errors.vin = VIN_LENGTH_ERROR;
  }
  if (
    values.agency_id.trim().length > 0 &&
    !/^\d+$/.test(values.agency_id.trim())
  ) {
    errors.agency_id = AGENCY_ID_ERROR;
  }

  return errors;
}

const orNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

/**
 * TowPro models heavy-duty towing as separate rotation regions rather than a
 * separate field, so the region name is the only signal for the dispatch
 * sub type (e.g. "Dane County — Heavy").
 */
export function deriveSubTypeFromRegionName(
  regionName: string | undefined,
): TowProSubType {
  return regionName && /heavy/i.test(regionName) ? 'Heavy' : 'Light';
}

function buildVehicleOwnerData(
  values: TowProFormValues,
): TowProVehicleOwnerData | null {
  const ownerData: TowProVehicleOwnerData = {
    firstName: orNull(values.owner_first_name),
    lastName: orNull(values.owner_last_name),
    street: orNull(values.owner_street),
    apt: orNull(values.owner_apt),
    city: orNull(values.owner_city),
    state: orNull(values.owner_state),
    zipCode: orNull(values.owner_zip),
  };

  const hasOwnerData = Object.values(ownerData).some(value => value !== null);
  return hasOwnerData ? ownerData : null;
}

/**
 * Map an existing tow request back to editable form values, the inverse of
 * {@link buildTowProDispatchRequest}.
 */
export function formValuesFromRequest(
  request: TowProRequestResponse,
): TowProFormValues {
  const data = request.request.data;
  const owner = data.vehicleOwnerData;

  return {
    address: data.addressInput.address1 ?? '',
    latitude: data.addressInput.location.latitude,
    longitude: data.addressInput.location.longitude,
    region_id: String(data.regionId),
    reason: data.reason,
    related_call: request.callerId ?? '',
    vin: data.vin ?? '',
    make: data.make ?? '',
    model: data.model ?? '',
    vehicle_year: data.vehicleYear ?? '',
    color: data.color ?? '',
    license_plate_number: data.licensePlateNumber ?? '',
    license_plate_state: data.licensePlateState ?? '',
    incident_number: data.incidentNumber ?? '',
    agency_id: data.agencyId != null ? String(data.agencyId) : '',
    officer_name: data.officerName ?? '',
    badge_number: data.badgeNumber ?? '',
    owner_first_name: owner?.firstName ?? '',
    owner_last_name: owner?.lastName ?? '',
    owner_phone: data.addressInput.phone ?? '',
    owner_street: owner?.street ?? '',
    owner_apt: owner?.apt ?? '',
    owner_city: owner?.city ?? '',
    owner_state: owner?.state ?? '',
    owner_zip: owner?.zipCode ?? '',
    notes: data.notes ?? '',
  };
}

/**
 * Map validated form values to the TowPro dispatch payload. Returns null when
 * the location or reason has not been resolved yet, which validation prevents
 * before submission.
 */
export function buildTowProDispatchRequest(
  values: TowProFormValues,
  regionName: string | undefined,
): TowProDispatchRequest | null {
  const reason = TOWPRO_TOW_REASONS.find(known => known === values.reason);
  if (values.latitude == null || values.longitude == null || !reason) {
    return null;
  }

  return {
    regionId: Number(values.region_id),
    addressInput: {
      address1: orNull(values.address),
      // TowPro keeps the vehicle owner's phone on the pickup address.
      phone: orNull(values.owner_phone),
      location: { latitude: values.latitude, longitude: values.longitude },
    },
    reason,
    subType: deriveSubTypeFromRegionName(regionName),
    sentToRotation: true,
    notes: orNull(values.notes),
    licensePlateNumber: orNull(values.license_plate_number),
    licensePlateState: orNull(values.license_plate_state),
    make: orNull(values.make),
    model: orNull(values.model),
    color: orNull(values.color),
    vehicleYear: orNull(values.vehicle_year),
    incidentNumber: orNull(values.incident_number),
    agencyId:
      values.agency_id.trim().length > 0 ? Number(values.agency_id) : null,
    vin: orNull(values.vin),
    officerName: orNull(values.officer_name),
    badgeNumber: orNull(values.badge_number),
    vehicleOwnerData: buildVehicleOwnerData(values),
  };
}
